import { IPoint2d } from './IPoint2d';
import { LineSeg2d } from './LineSeg2d';
import { Point2dUtils } from './Point2dUtils';
import { Circle2d } from './circle2d';
import { arcThresholdValue, thresholdValue } from './const';
import { Ray2d } from './ray2d';
import { equalsNumber, normalizeAngle } from './utils';

const MAX_CHORD_ERROR = 2; // 允许的最大弦误差，mm

interface IUniformInfo {
  center: IPoint2d;
  radius: number;
  startAngle: number;
  endAngle: number;
  clockwise: boolean;
}

export class ArcSeg2d {
  private _start: IPoint2d;
  private _end: IPoint2d;

  // private sagitta: number; // 弦在左侧时，弦高为正，弦在右侧时，弦高为负
  // private _start: IPoint2d;
  // private _end: IPoint2d;
  private _center: IPoint2d;
  private _radius: number;
  private _startAngle: number; // 相对于+x轴的弧度
  private _endAngle: number;
  private _clockwise: boolean;

  constructor(
    options:
      | { start: IPoint2d; end: IPoint2d; thirdPoint: IPoint2d }
      | { start: IPoint2d; end: IPoint2d; sagitta: number }
      | IUniformInfo
      | (Omit<IUniformInfo, 'startAngle' | 'endAngle' | 'radius'> & { start: IPoint2d; end: IPoint2d })
  ) {
    if ('thirdPoint' in options) {
      // 已知弦上点
      const uniformInfo = ArcSeg2d.calculateUniformInfoFromThridPoint(options.start, options.end, options.thirdPoint);
      this._center = uniformInfo.center;
      this._radius = uniformInfo.radius;
      this._startAngle = uniformInfo.startAngle;
      this._endAngle = uniformInfo.endAngle;
      this._clockwise = uniformInfo.clockwise;
      this._start = options.start;
      this._end = options.end;
    } else if ('sagitta' in options) {
      // 已知弦高
      const uniformInfo = ArcSeg2d.calculateUniformInfoFromSagitta(options.start, options.end, options.sagitta);
      this._center = uniformInfo.center;
      this._radius = uniformInfo.radius;
      this._startAngle = uniformInfo.startAngle;
      this._endAngle = uniformInfo.endAngle;
      this._clockwise = uniformInfo.clockwise;
      this._start = options.start;
      this._end = options.end;
    } else if ('start' in options && 'center' in options) {
      this._startAngle = normalizeAngle(
        Math.atan2(options.start.y - options.center.y, options.start.x - options.center.x)
      );
      this._endAngle = normalizeAngle(Math.atan2(options.end.y - options.center.y, options.end.x - options.center.x));
      this._clockwise = options.clockwise;
      this._center = options.center;
      this._radius = Point2dUtils.distanceTo(options.start, options.center);
      this._start = options.start;
      this._end = options.end;
    } else {
      // 已知圆心、半径、起始角度、结束角度、顺时针方向
      this._center = options.center;
      this._radius = options.radius;
      this._startAngle = normalizeAngle(options.startAngle);
      this._endAngle = normalizeAngle(options.endAngle);
      this._clockwise = options.clockwise;
      [this._start, this._end] = this.getExtremePoints();
    }
  }

  // 计算弦高
  static calculateSagitta(_start: IPoint2d, _end: IPoint2d, point: IPoint2d): number {
    const x1 = _start.x;
    const y1 = _start.y;
    const x2 = _end.x;
    const y2 = _end.y;
    const x3 = point.x;
    const y3 = point.y;

    const a2 = (x2 - x3) ** 2 + (y2 - y3) ** 2;
    const b2 = (x1 - x3) ** 2 + (y1 - y3) ** 2;
    const c2 = (x2 - x1) ** 2 + (y2 - y1) ** 2;

    const a = Math.sqrt(a2);
    const b = Math.sqrt(b2);
    const c = Math.sqrt(c2);

    // 叉积计算有向面积的两倍
    const D = (x2 - x1) * (y3 - y1) - (x3 - x1) * (y2 - y1);

    // 如果三点共线，则弦高为0
    if (Math.abs(D) < 1e-6) {
      return 0;
    }

    // 计算外接圆半径 R = (a * b * c) / (4 * Area), Area = |D| / 2
    const R = (a * b * c) / (2 * Math.abs(D));

    // 点积判断P点所在角度是否为钝角或锐角
    // 若点积 > 0，则为锐角，说明圆弧为优弧（大于半圆）
    // 若点积 <= 0，则为钝角或直角，说明圆弧为劣弧（小于等于半圆）
    const dot = (x1 - x3) * (x2 - x3) + (y1 - y3) * (y2 - y3);

    // 圆心到弦的距离
    const d2 = R * R - c2 / 4;
    const d = Math.sqrt(Math.max(0, d2));

    const h = dot > 0 ? R + d : R - d;

    // 根据叉积确定方向（正负号表示在左侧还是右侧）
    // 注意：这里的方向与我们使用的坐标系有关（canvas y轴向下），这里只是作为符号参考
    return D > 0 ? h : -h;
  }

  static calculateUniformInfoFromThridPoint(start: IPoint2d, end: IPoint2d, thirdPoint: IPoint2d): IUniformInfo {
    const sagitta = ArcSeg2d.calculateSagitta(start, end, thirdPoint);
    return ArcSeg2d.calculateUniformInfoFromSagitta(start, end, sagitta);
  }

  static calculateUniformInfoFromSagitta(start: IPoint2d, end: IPoint2d, sagitta: number): IUniformInfo {
    const chordVecX = end.x - start.x;
    const chordVecY = end.y - start.y;
    const chordLength = Math.hypot(chordVecX, chordVecY);
    const sagittaAbs = Math.abs(sagitta);

    const halfChord = chordLength / 2;
    const radius = (halfChord * halfChord) / (2 * sagittaAbs) + sagittaAbs / 2;

    // 弦中点
    const midX = (start.x + end.x) / 2;
    const midY = (start.y + end.y) / 2;

    // 弦方向的单位法向量（弦方向逆时针旋转 90°，即弦左侧方向）
    const perpX = -chordVecY / chordLength;
    const perpY = chordVecX / chordLength;

    // 圆心在弦中点沿法向量方向偏移的有符号距离
    // sagitta > 0 表示弧凸向弦左侧，圆心在弦右侧（负方向）
    const sign = sagitta > 0 ? 1 : -1;
    const t = -(radius - sagittaAbs) * sign;
    const center: IPoint2d = { x: midX + t * perpX, y: midY + t * perpY };

    return {
      center,
      radius,
      startAngle: normalizeAngle(Math.atan2(start.y - center.y, start.x - center.x)),
      endAngle: normalizeAngle(Math.atan2(end.y - center.y, end.x - center.x)),
      clockwise: sign === 1
    };
  }

  get start(): IPoint2d {
    return this._start;
  }

  get end(): IPoint2d {
    return this._end;
  }

  get center(): IPoint2d {
    return this._center;
  }

  get radius(): number {
    return this._radius;
  }

  get startAngle(): number {
    return this._startAngle;
  }

  get endAngle(): number {
    return this._endAngle;
  }

  get clockwise(): boolean {
    return this._clockwise;
  }

  get length(): number {
    return this._radius * this.getSweepAngle();
  }

  getSagitta(): number {
    const { _center, _radius, _startAngle, _clockwise } = this;
    const [start, end] = this.getExtremePoints();
    const midAngle = _clockwise ? _startAngle - this.getSweepAngle() / 2 : _startAngle + this.getSweepAngle() / 2;
    const midArcPoint = {
      x: _center.x + _radius * Math.cos(midAngle),
      y: _center.y + _radius * Math.sin(midAngle)
    };
    const midChordPoint = {
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2
    };

    return Point2dUtils.distanceTo(midChordPoint, midArcPoint) * (_clockwise ? 1 : -1);
  }

  getExtremePoints(): [start: IPoint2d, end: IPoint2d] {
    const { _center, _radius, _startAngle, _endAngle } = this;
    return [
      {
        x: _center.x + _radius * Math.cos(_startAngle),
        y: _center.y + _radius * Math.sin(_startAngle)
      },
      {
        x: _center.x + _radius * Math.cos(_endAngle),
        y: _center.y + _radius * Math.sin(_endAngle)
      }
    ];
  }

  // 获取扫角（圆心角），正值
  getSweepAngle(): number {
    const { _startAngle, _endAngle, _clockwise } = this;

    let sweep = _clockwise ? _startAngle - _endAngle : _endAngle - _startAngle;
    if (sweep < 0) sweep += 2 * Math.PI;
    return sweep;
  }

  // 获取点相对于弧的起始点的扫角
  getPointSweepAngle(point: IPoint2d): number {
    const { _center, _radius, _startAngle, _clockwise } = this;
    const angle = normalizeAngle(Math.atan2(point.y - _center.y, point.x - _center.x));
    return _clockwise ? normalizeAngle(_startAngle - angle) : normalizeAngle(angle - _startAngle);
  }

  // 半径偏移
  getOffsetArc(offset: number) {
    const { _center, _radius, _startAngle, _endAngle, _clockwise } = this;

    return new ArcSeg2d({
      startAngle: _startAngle,
      endAngle: _endAngle,
      clockwise: _clockwise,
      center: _center,
      radius: _radius + offset
    });
  }

  isOverlap(other: ArcSeg2d): boolean {
    if (!Point2dUtils.equals(this._center, other._center)) return false;

    if (!equalsNumber(this._radius, other._radius)) return false;

    // 判断角度 angle 是否严格在弧 arc 的角度范围内（不含端点）
    const angleStrictlyInArc = (angle: number, arc: ArcSeg2d): boolean => {
      const sweep = arc.getSweepAngle();
      // 对于整圆（sweep ≈ 2π）直接判定包含
      if (Math.abs(sweep - 2 * Math.PI) < thresholdValue) return true;
      const diff = arc.clockwise ? normalizeAngle(arc.startAngle - angle) : normalizeAngle(angle - arc.startAngle);
      return diff > 1e-9 && diff < sweep - 1e-9;
    };

    // 任意一方的端点严格在另一方弧内，则两弧有重叠区间
    return (
      angleStrictlyInArc(other.startAngle, this) ||
      angleStrictlyInArc(other.endAngle, this) ||
      angleStrictlyInArc(this._startAngle, other) ||
      angleStrictlyInArc(this._endAngle, other)
    );
  }

  pointOnSegment(point: IPoint2d): boolean {
    // 1. 点到圆心距离必须等于半径
    const dist = Point2dUtils.distanceTo(this._center, point);
    if (Math.abs(dist - this._radius) > thresholdValue) return false;

    // 2. 点的角度必须在弧的角度范围内（含端点）
    const angle = normalizeAngle(Math.atan2(point.y - this._center.y, point.x - this._center.x));
    const sweep = this.getSweepAngle();
    const diff = this._clockwise ? normalizeAngle(this._startAngle - angle) : normalizeAngle(angle - this._startAngle);
    return diff <= sweep + thresholdValue;
  }

  getDefaulSubdivisionAmount(): number {
    const { _radius } = this;
    const sweepAngle = this.getSweepAngle();
    const anglePerSeg = 2 * Math.acos(1 - MAX_CHORD_ERROR / _radius);
    return Math.max(4, Math.min(500, Math.ceil(sweepAngle / anglePerSeg)));
  }

  // 离散化弧，返回直线段
  getPoints(segmentCount?: number) {
    const { _center, _radius, _startAngle, _endAngle, _clockwise } = this;
    const sweepAngle = this.getSweepAngle();

    if (!segmentCount) {
      segmentCount = this.getDefaulSubdivisionAmount();
    }

    const shapePts: IPoint2d[] = [];
    const deltaAngle = (sweepAngle / segmentCount) * (_clockwise ? -1 : 1);
    for (let i = 0; i <= segmentCount; i++) {
      const angle = _startAngle + deltaAngle * i;
      shapePts.push({
        x: _center.x + _radius * Math.cos(angle),
        y: _center.y + _radius * Math.sin(angle)
      });
    }

    return shapePts;
  }

  equals(other: ArcSeg2d): boolean {
    return (
      Point2dUtils.equals(this.center, other.center) &&
      equalsNumber(this.radius, other.radius) &&
      equalsNumber(this.startAngle, other.startAngle) &&
      equalsNumber(this.endAngle, other.endAngle) &&
      this.clockwise === other.clockwise
    );
  }

  // 判断点P相对于圆环的位置
  pointSide(point: IPoint2d): number {
    const { _center, _radius, _startAngle, _endAngle, _clockwise } = this;
    const distance = Point2dUtils.distanceTo(_center, point);

    if (distance === _radius) return 0;

    return (distance > _radius ? 1 : -1) * (_clockwise ? 1 : -1);
  }

  getPerpendicularLine(point: IPoint2d): LineSeg2d {
    return new LineSeg2d(point, this.center);
  }

  createCircle(): Circle2d {
    return new Circle2d({ center: this.center, radius: this.radius });
  }

  // 创建start/end 作为起点的径向射线
  createRadialRay(startPointAsOrigin: boolean = true, antiRadial: boolean = false): Ray2d {
    const origin = startPointAsOrigin ? this._start : this._end;
    let direction = Point2dUtils.getDirection(this.center, origin);
    if (antiRadial) direction = { x: -direction.x, y: -direction.y };
    return new Ray2d(origin, direction);
  }

  isTangentTo(other: ArcSeg2d): boolean {
    const mainCircle = this.createCircle();
    const otherCircle = other.createCircle();
    const intersectPoints = mainCircle.intersect(otherCircle);
    if (intersectPoints) {
      if (intersectPoints.length === 1) {
        return true;
      } else if (intersectPoints.length === 2) {
        if (Point2dUtils.equals(intersectPoints[0], intersectPoints[1], arcThresholdValue)) {
          return true;
        }
      }
    }

    return false;
  }

  intersect(other: ArcSeg2d): IPoint2d[] | null {
    const c1 = new Circle2d({ center: this._center, radius: this._radius });
    const c2 = new Circle2d({ center: other._center, radius: other._radius });
    const candidates = c1.intersect(c2);

    if (!candidates || candidates.length === 0) return null;

    const angleInArc = (angle: number, arc: ArcSeg2d): boolean => {
      const sweep = arc.getSweepAngle();
      const diff = arc.clockwise ? normalizeAngle(arc.startAngle - angle) : normalizeAngle(angle - arc.startAngle);
      return diff <= sweep + thresholdValue;
    };

    const result = candidates.filter((pt: IPoint2d) => {
      const a1 = normalizeAngle(Math.atan2(pt.y - this._center.y, pt.x - this._center.x));
      const a2 = normalizeAngle(Math.atan2(pt.y - other._center.y, pt.x - other._center.x));
      return angleInArc(a1, this) && angleInArc(a2, other);
    });

    return result.length > 0 ? result : null;
  }

  // 切角
  getTangentAngle(point: IPoint2d): number {
    const direction = Point2dUtils.getDirection(this.center, point);
    const tangentDirection = Point2dUtils.rotateDirection(direction, (Math.PI / 2) * (this.clockwise ? -1 : 1));
    return normalizeAngle(Math.atan2(tangentDirection.y, tangentDirection.x));
  }

  // 获取反向弧
  getOppositeArc(): ArcSeg2d {
    return new ArcSeg2d({ start: this.end, end: this.start, center: this.center, clockwise: !this.clockwise });
  }

  getOverlapSegment(other: ArcSeg2d): ArcSeg2d | null {
    if (!Point2dUtils.equals(this._center, other._center)) return null;
    if (!equalsNumber(this._radius, other._radius)) return null;

    const TWO_PI = 2 * Math.PI;
    const sweepA = this.getSweepAngle();
    const sweepB = other.getSweepAngle();

    // 将两段弧线统一归一化为 CCW 区间：
    // CW 弧的 CCW 起始角 = endAngle（从 end 逆时针走到 start 覆盖相同角度范围）
    const ccwStartA = this._clockwise ? this._endAngle : this._startAngle;
    const ccwStartB = other._clockwise ? other._endAngle : other._startAngle;

    // 平移坐标系使 A 从 0 开始，B 的归一化起始角
    const normB = normalizeAngle(ccwStartB - ccwStartA);

    let overlapStart: number;
    let overlapEnd: number;

    if (normB + sweepB <= TWO_PI) {
      // B 在归一化坐标系中不发生环绕，直接求线性区间交集
      overlapStart = Math.max(0, normB);
      overlapEnd = Math.min(sweepA, normB + sweepB);
    } else {
      // B 发生环绕：覆盖 [normB, 2π) ∪ [0, normB + sweepB - 2π]
      // 与 A = [0, sweepA] 的交集分两段：
      //   part1: [normB, sweepA]（若 normB < sweepA）
      //   part2: [0, min(sweepA, normB + sweepB - 2π)]
      const p1Len = normB < sweepA ? sweepA - normB : 0;
      const p2End = Math.min(sweepA, normB + sweepB - TWO_PI);
      const p2Len = p2End > 0 ? p2End : 0;

      // 取较长的一段作为重叠结果
      if (p1Len >= p2Len) {
        overlapStart = normB;
        overlapEnd = sweepA;
      } else {
        overlapStart = 0;
        overlapEnd = p2End;
      }
    }

    if (overlapEnd - overlapStart <= thresholdValue) return null;

    // 将归一化坐标系中的区间转换回绝对角度（CCW）
    const overlapCcwStart = normalizeAngle(overlapStart + ccwStartA);
    const overlapCcwEnd = normalizeAngle(overlapCcwStart + (overlapEnd - overlapStart));

    // 按照 this 的方向返回重叠弧
    if (this._clockwise) {
      return new ArcSeg2d({
        center: this._center,
        radius: this._radius,
        startAngle: overlapCcwEnd,
        endAngle: overlapCcwStart,
        clockwise: true
      });
    } else {
      return new ArcSeg2d({
        center: this._center,
        radius: this._radius,
        startAngle: overlapCcwStart,
        endAngle: overlapCcwEnd,
        clockwise: false
      });
    }
  }

  // 获取弧中间点
  getMidPoint(): IPoint2d {
    const { _center, _radius, _startAngle, _clockwise } = this;
    const midAngle = _clockwise ? _startAngle - this.getSweepAngle() / 2 : _startAngle + this.getSweepAngle() / 2;
    return {
      x: _center.x + _radius * Math.cos(midAngle),
      y: _center.y + _radius * Math.sin(midAngle)
    };
  }

  getFootRatio(point: IPoint2d): number {
    const { _center, _startAngle, _endAngle, _clockwise } = this;
    const sweepAngle = this.getSweepAngle();
    const angle = normalizeAngle(Math.atan2(point.y - _center.y, point.x - _center.x));
    const diff = _clockwise ? normalizeAngle(_startAngle - angle) : normalizeAngle(angle - _startAngle);
    const ratio = diff / sweepAngle;

    const startEndGap = normalizeAngle(_clockwise ? _endAngle - _startAngle : _startAngle - _endAngle);

    if (diff > sweepAngle + startEndGap / 2) return 1 - ratio;
    return ratio;
  }

  getExactFootRatio(point: IPoint2d): number {
    const { _center, _startAngle, _endAngle, _clockwise } = this;
    const sweepAngle = this.getSweepAngle();
    const angle = normalizeAngle(Math.atan2(point.y - _center.y, point.x - _center.x));

    // 从起点开始，顺着绘制方向到目标点的正向角度差 (范围 0 到 2π)
    const diff = _clockwise ? normalizeAngle(_startAngle - angle) : normalizeAngle(angle - _startAngle);

    const PI2 = Math.PI * 2; // 一整圈的角度
    const gap = PI2 - sweepAngle; // 圆弧首尾之间的空白夹角

    // 如果该点落在空白区域的后半段
    if (diff > sweepAngle + gap / 2) {
      const exactNegativeDiff = diff - PI2;
      return exactNegativeDiff / sweepAngle; // 返回精确的负数 ratio
    }

    // 如果点在圆弧内，或者在空白区域的前半段（更靠近终点）
    // 直接返回真实的超长正向比例（> 1）
    return diff / sweepAngle;
  }

  getPointFromRatio(ratio: number): IPoint2d {
    const { _center, _radius, _startAngle, _clockwise } = this;
    const angle = normalizeAngle(_startAngle + ratio * this.getSweepAngle() * (_clockwise ? -1 : 1));

    return {
      x: _center.x + _radius * Math.cos(angle),
      y: _center.y + _radius * Math.sin(angle)
    };
  }

  // TODO: 还有问题
  subtractSegments(subtractSegs: ArcSeg2d[]): ArcSeg2d[] {
    // 以逆时针、mainStartAngle为基准做计算
    const mainSweepAngle = this.getSweepAngle();
    const mainReferenceStartAngle = this._clockwise ? this._endAngle : this._startAngle;
    const mainSections = [[0, mainSweepAngle]];

    subtractSegs = subtractSegs.filter(
      (seg) => Point2dUtils.equals(this.center, seg.center) && equalsNumber(this.radius, seg.radius)
    );

    for (const subtractSeg of subtractSegs) {
      const subtractSweepAngle = subtractSeg.getSweepAngle();
      let subtractStartAngle = subtractSeg._clockwise ? subtractSeg._endAngle : subtractSeg._startAngle;
      subtractStartAngle = normalizeAngle(subtractStartAngle - mainReferenceStartAngle);
      const subtractEndAngle = normalizeAngle(subtractStartAngle + subtractSweepAngle);

      const subSections: [number, number][] = [];
      if (subtractEndAngle > subtractStartAngle) {
        if (subtractStartAngle < mainSweepAngle) {
          subSections.push([subtractStartAngle, Math.min(subtractEndAngle, mainSweepAngle)]);
        }
      } else if (subtractEndAngle < subtractStartAngle) {
        if (subtractEndAngle < mainSweepAngle) {
          subSections.push([0, Math.min(subtractStartAngle, mainSweepAngle)]);
        }
        if (subtractStartAngle < mainSweepAngle) {
          subSections.push([subtractStartAngle, mainSweepAngle]);
        }
      }

      for (const subSection of subSections) {
        if (mainSections.length === 0) return [];

        let startIndex = -1; // -1表示超出mainSections左侧，mainSections.length表示超出mainSections右侧
        let endIndex = -1;

        if (subSection[0] <= mainSections[0][0]) startIndex = -1;
        else if (subSection[0] >= mainSections[mainSections.length - 1][1]) startIndex = mainSections.length;
        else
          startIndex = mainSections.findIndex((section) => subSection[0] >= section[0] && subSection[0] < section[1]);

        if (subSection[1] <= mainSections[0][0]) endIndex = -1;
        else if (subSection[1] >= mainSections[mainSections.length - 1][1]) endIndex = mainSections.length;
        else endIndex = mainSections.findIndex((section) => subSection[1] >= section[0] && subSection[1] < section[1]);

        // 开始减
        if (startIndex === -1 && endIndex === -1) continue;
        if (startIndex === mainSections.length && endIndex === mainSections.length) continue;
        if (startIndex === -1 && endIndex === mainSections.length) return [];
        if (startIndex === -1) {
          mainSections.splice(0, endIndex);
          mainSections[0][0] = subSection[1];
          continue;
        }
        if (endIndex === mainSections.length) {
          mainSections.splice(startIndex + 1);
          mainSections[mainSections.length - 1][1] = subSection[0];
          continue;
        }

        if (startIndex === endIndex) {
          const section = mainSections[startIndex];
          mainSections.splice(startIndex, 1);
          mainSections.splice(startIndex, 0, [section[0], subSection[0]], [subSection[1], section[1]]);
          continue;
        }

        const startIndexSection = mainSections[startIndex];
        const endIndexSection = mainSections[endIndex];
        startIndexSection[1] = subSection[0];
        endIndexSection[0] = subSection[1];
        mainSections.splice(startIndex + 1, endIndex - startIndex - 1);
      }
    }

    return mainSections.map((section) => {
      let startAngle = normalizeAngle(section[0] + mainReferenceStartAngle);
      let endAngle = normalizeAngle(section[1] + mainReferenceStartAngle);
      if (this._clockwise) [startAngle, endAngle] = [endAngle, startAngle];
      return new ArcSeg2d({
        center: this._center,
        radius: this._radius,
        startAngle,
        endAngle,
        clockwise: this._clockwise
      });
    });
  }
}
